import { useEffect, useState } from "react";

const POLL_INTERVAL_MS = 60_000;

/**
 * Polls /app-config.js periodically with a cache-busting query string and
 * returns the buildCommit value found in the freshly-fetched response.
 *
 * Why: /app-config.js is re-generated on every CDK deploy with the current
 * GitHub SHA (see infra/otto-dealer-bot-stack.ts buildCommit). By fetching
 * it with a cache-buster we always see the LATEST deployed commit, even
 * when CloudFront has cached the index.html that holds our currently-
 * running bundle. Comparing the polled value against the bundle's own
 * baked-in commit (import.meta.env.VITE_BUILD_COMMIT) lets us detect when
 * a fresh deploy has shipped and prompt the user to refresh.
 *
 * Returns null while the first poll is in flight or if every poll has
 * failed (network blip, S3/CloudFront 5xx). Caller should treat null as
 * 'no info' rather than 'no new version'.
 */
export function useDeployedVersion(): string | null {
  const [deployedCommit, setDeployedCommit] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function poll() {
      try {
        const res = await fetch(`/app-config.js?_=${Date.now()}`, {
          cache: "no-store",
          credentials: "omit",
        });
        if (!res.ok) return;
        const text = await res.text();
        const match = /"buildCommit"\s*:\s*"([^"]+)"/.exec(text);
        if (match && !cancelled) {
          setDeployedCommit(match[1]);
        }
      } catch {
        // Network blip; will retry on next interval. Swallowing keeps the
        // poll loop alive instead of erroring out the whole banner.
      }
    }

    void poll();
    const handle = window.setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, []);

  return deployedCommit;
}
